<script lang="ts" setup>
/**
 * 动态封面视频层（详情页 / 游戏 Loading 页共用）
 * 静态封面始终垫底，视频出首帧后淡入盖在上面；
 * URL 为空、加载/播放失败、自动播放被拒、5 秒内未出首帧、切后台恢复失败
 * 均静默隐藏视频层，露出下方静态封面（接口降级链见对接文档）
 */
import { onMounted, onUnmounted, ref, watch } from 'vue'

const props = defineProps<{
  src: string
}>()

// 首帧超时：超过该时间未能播放则放弃视频，保持静态图
const FIRST_FRAME_TIMEOUT_MS = 5000

const videoRef = ref<HTMLVideoElement>()
const visible = ref(false)
const failed = ref(false)
let timeoutTimer: ReturnType<typeof setTimeout> | null = null

function clearTimer() {
  if (timeoutTimer) {
    clearTimeout(timeoutTimer)
    timeoutTimer = null
  }
}

function fail() {
  clearTimer()
  visible.value = false
  failed.value = true
}

function onPlaying() {
  clearTimer()
  visible.value = true
}

function tryPlay() {
  const video = videoRef.value
  if (!video || failed.value)
    return
  // muted 需在 play 前用 JS 置属性（Vue 2 模板 attribute 不可靠），否则移动端自动播放被拒
  video.muted = true
  video.play().catch(fail)
}

function start() {
  clearTimer()
  visible.value = false
  failed.value = !props.src
  if (failed.value)
    return
  timeoutTimer = setTimeout(fail, FIRST_FRAME_TIMEOUT_MS)
  tryPlay()
}

// 切后台暂停后回前台恢复播放，恢复失败走同一降级链
function onVisibilityChange() {
  if (!document.hidden)
    tryPlay()
}

onMounted(() => {
  start()
  document.addEventListener('visibilitychange', onVisibilityChange)
})

onUnmounted(() => {
  clearTimer()
  document.removeEventListener('visibilitychange', onVisibilityChange)
})

watch(() => props.src, () => {
  // v-if 重建 video 元素后再启动，确保新 src 生效
  requestAnimationFrame(start)
})
</script>

<template>
  <video
    v-if="src && !failed"
    ref="videoRef"
    :src="src"
    class="biz-cover-video"
    :class="{ 'is-visible': visible }"
    autoplay
    loop
    muted
    playsinline
    webkit-playsinline
    preload="auto"
    @playing="onPlaying"
    @error="fail"
  />
</template>

<style lang="scss" scoped>
/* 与静态封面层同规则：宽度铺满、高度按视频比例自适应、顶对齐，仅作背景不接收交互
   （不用 height:100% + contain，避免视频比屏幕更"高"时左右留半像素空隙露底色） */
.biz-cover-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;

  &.is-visible {
    opacity: 1;
  }
}
</style>
